import { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, ShoppingBag, IndianRupee, TicketPercent } from "lucide-react";
import { Button, Table, Row, Col, Typography, Grid } from "antd";
import {
  fetchDiscountCodes,
  getDiscountCodesSummary,
} from "services/discountCodes.service";
import { renderErrorNotifications } from "helpers/error.helpers";
import useDiscountCodesConfig from "hooks/useDiscountCodesConfig/useDiscountCodesConfig";
import useDebouncedCallback from "hooks/useDebouncedCallback/useDebouncedCallback";
import CodesFilterBar from "./_blocks/CodesFilterBar";
import CodeTiles from "./_blocks/CodeTiles";
import CodeFormModal from "./_blocks/CodeFormModal";
import useVendorEvents from "./commons/useVendorEvents";
import useCodeActions from "./commons/useCodeActions";
import { getCodeListColumns } from "./commons/discountCodes.columns";
import {
  CODES_PAGE_SIZE,
  CODE_STATUS_FILTER_OPTIONS,
  buildEventOptions,
  filterPortalCodes,
  formatRupees,
  getListSubtitle,
} from "./commons/discountCodes.helpers";
import { CODES_COPY } from "./commons/discountCodes.copy";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const isKnownStatus = (s) =>
  CODE_STATUS_FILTER_OPTIONS.some((o) => o.value === s);

function DiscountCodesList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { policy, communities } = useDiscountCodesConfig();
  const { events, loading: loadingEvents } = useVendorEvents();

  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [modal, setModal] = useState({ open: false, codeId: null });
  const [search, setSearch] = useState(searchParams.get("q") ?? "");
  const [filters, setFilters] = useState(() => ({
    q: searchParams.get("q") || null,
    status: isKnownStatus(searchParams.get("status"))
      ? searchParams.get("status")
      : null,
    event_id: searchParams.get("event_id") || null,
    community_id: searchParams.get("community_id") || null,
  }));
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: CODES_PAGE_SIZE,
    total: 0,
  });

  const mergeParams = (updates) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([k, v]) => {
        if (!v) next.delete(k);
        else next.set(k, v);
      });
      return next;
    });
  };

  const loadCodes = useCallback(async (page, f) => {
    setLoading(true);
    const { data: envelope, errors } = await fetchDiscountCodes({
      current_page: page,
      page_size: CODES_PAGE_SIZE,
      ...(f.q && { search: f.q }),
      ...(f.status && { status: f.status }),
      ...(f.event_id && { event_id: f.event_id }),
      ...(f.community_id && { community_id: f.community_id }),
    });
    setLoading(false);
    if (errors?.length) {
      renderErrorNotifications(errors);
      setCodes([]);
      return;
    }
    // Event codes only (D78 R11): other kinds are never shown here.
    setCodes(
      filterPortalCodes(Array.isArray(envelope?.data) ? envelope.data : [])
    );
    const meta = envelope?.meta?.pagination ?? {};
    setPagination((prev) => ({
      ...prev,
      current: page,
      total: meta.total_count ?? 0,
    }));
  }, []);

  const loadSummary = useCallback(async (communityId) => {
    setLoadingSummary(true);
    const { data, errors } = await getDiscountCodesSummary({
      community_id: communityId,
    });
    setLoadingSummary(false);
    if (errors?.length) {
      renderErrorNotifications(errors);
      setSummary(null);
      return;
    }
    setSummary(data);
  }, []);

  useEffect(() => {
    loadCodes(1, filters);
  }, [filters, loadCodes]);

  useEffect(() => {
    loadSummary(filters.community_id);
  }, [filters.community_id, loadSummary]);

  const applyFilters = (updates) => {
    setFilters((prev) => ({ ...prev, ...updates }));
    mergeParams(updates);
  };

  // Server-side search, debounced 300 ms (R-8).
  const debouncedSearch = useDebouncedCallback(
    (value) => applyFilters({ q: value?.trim() || null }),
    300
  );

  const handleSearchChange = (value) => {
    setSearch(value);
    debouncedSearch(value);
  };

  const reload = () => {
    loadCodes(pagination.current, filters);
    loadSummary(filters.community_id);
  };

  const { runAction, contextHolder } = useCodeActions({ onDone: reload });
  const screens = useBreakpoint();

  const columns = getCodeListColumns({
    onEdit: (row) => setModal({ open: true, codeId: row.code_id }),
    onAction: runAction,
  });

  const eventOptions = useMemo(
    () => buildEventOptions(events, { communityId: filters.community_id }),
    [events, filters.community_id]
  );
  const communityOptions = communities.map((c) => ({
    value: c.community_id,
    label: c.display_name,
  }));

  const hasActiveFilters = Boolean(
    filters.q || filters.status || filters.event_id || filters.community_id
  );

  const tiles = [
    {
      key: "bookings",
      label: CODES_COPY.tileBookings,
      icon: ShoppingBag,
      colorClass: "bg-blue-100 text-blue-600",
      value: Number(summary?.bookings_with_code ?? 0).toLocaleString("en-IN"),
    },
    {
      key: "revenue",
      label: CODES_COPY.tileRevenue,
      icon: IndianRupee,
      colorClass: "bg-emerald-100 text-emerald-600",
      value: formatRupees(summary?.revenue_from_those_bookings),
    },
    {
      key: "discount",
      label: CODES_COPY.tileDiscount,
      icon: TicketPercent,
      colorClass: "bg-amber-100 text-amber-600",
      value: formatRupees(summary?.discount_given),
    },
  ];

  return (
    <div className="space-y-6">
      <Row justify="space-between" align="middle" gutter={[16, 16]}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            {CODES_COPY.listTitle}
          </Title>
          <Text type="secondary">{getListSubtitle(policy)}</Text>
          <div>
            <Text type="secondary" className="text-sm">
              {CODES_COPY.listAppNote}
            </Text>
          </div>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<Plus size={14} />}
            onClick={() => setModal({ open: true, codeId: null })}
          >
            {CODES_COPY.cta}
          </Button>
        </Col>
      </Row>

      <CodeTiles tiles={tiles} loading={loadingSummary} />

      <CodesFilterBar
        search={search}
        onSearchChange={handleSearchChange}
        statusFilter={filters.status}
        onStatusChange={(v) => applyFilters({ status: v ?? null })}
        eventFilter={filters.event_id}
        onEventChange={(v) => applyFilters({ event_id: v ?? null })}
        eventOptions={eventOptions}
        loadingEvents={loadingEvents}
        communityFilter={filters.community_id}
        onCommunityChange={(v) =>
          applyFilters({ community_id: v ?? null, event_id: null })
        }
        communityOptions={communityOptions}
      />

      {!loading && codes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center space-y-4 bg-card rounded-2xl border border-dashed">
          <TicketPercent className="h-10 w-10 text-foreground/50" />
          <div>
            <Title level={4}>
              {hasActiveFilters
                ? CODES_COPY.emptyFilteredTitle
                : CODES_COPY.emptyTitle}
            </Title>
            <Text type="secondary">
              {hasActiveFilters
                ? CODES_COPY.emptyFilteredBody
                : CODES_COPY.emptyBody}
            </Text>
          </div>
          {!hasActiveFilters && (
            <Button
              icon={<Plus size={14} />}
              onClick={() => setModal({ open: true, codeId: null })}
            >
              {CODES_COPY.cta}
            </Button>
          )}
        </div>
      ) : (
        // The wrapper's width ignores the table's, so on a phone the table
        // scrolls inside it instead of widening the page (S-237).
        <div className="[contain:inline-size]">
          <Table
            rowKey="code_id"
            columns={columns}
            dataSource={codes}
            loading={loading}
            size="small"
            scroll={{ x: screens.md ? 900 : "max-content" }}
            pagination={{
              current: pagination.current,
              pageSize: CODES_PAGE_SIZE,
              total: pagination.total,
              showSizeChanger: false,
              placement: "bottomRight",
            }}
            onChange={(pag) => loadCodes(pag.current, filters)}
          />
        </div>
      )}

      {contextHolder}

      <CodeFormModal
        open={modal.open}
        codeId={modal.codeId}
        onClose={() => setModal({ open: false, codeId: null })}
        onSaved={reload}
      />
    </div>
  );
}

export default DiscountCodesList;
